import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Phone, User, MapPin, Mail, Shield, CheckCircle2, Award, Clock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { provinces } from '../mockData';
import { getCitta, getLeadTrackingFields } from '../utils/leadTracking';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HeroSection = () => {
  const navigate = useNavigate();
  const citta = getCitta();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [quickForm, setQuickForm] = useState({ name: '', phone: '', email: '', province: '', website: '' });

  const handleQuickSubmit = async (e) => {
    e.preventDefault();
    if (!quickForm.name || !quickForm.phone || !quickForm.email || !quickForm.province) { toast.error('Compila tutti i campi'); return; }
    setIsSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/leads`, {
        name: quickForm.name,
        phone: quickForm.phone,
        email: quickForm.email,
        province: quickForm.province,
        houseType: 'Da definire',
        ownershipStatus: 'Da definire',
        problemDescription: 'Richiesta rapida da Hero',
        website: quickForm.website,
        ...getLeadTrackingFields(),
      });
      if (typeof window.gtag === 'function') window.gtag('event', 'conversion', { 'send_to': 'AW-17960264240/HNy_CMqrr_obELDEj_RC', 'value': 1.0, 'currency': 'EUR' });
      if (typeof window.fbq === 'function') window.fbq('track', 'Lead');
      navigate('/grazie');
    } catch { toast.error("Errore nell'invio. Riprova."); }
    finally { setIsSubmitting(false); }
  };

  return (
    <section id="sopralluogo" className="relative min-h-[90vh] flex items-center overflow-hidden bg-[#0F172A] pt-32 pb-20">
      <div className="absolute inset-0">
        <img src="/brochure/tecnico-posa-coppi.jpg" alt="Tecnico Evoluzione Tetto al lavoro sulla copertura" className="w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A] via-[#0F172A]/80 to-[#0F172A]/40" />
      </div>
      <div className="container max-w-7xl mx-auto px-6 md:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7 }} className="lg:col-span-7 text-center lg:text-left">
            <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 bg-emerald-500/15 border border-emerald-500/25 rounded-full">
              <Award className="w-4 h-4 text-emerald-400" />
              <span className="text-emerald-400 font-medium text-sm">Certificati ISO 9001 | Garanzia fino a 10 anni</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-6 leading-[1.1]" data-testid="hero-title">
              <span className="text-white">Manutenzione Tetti{citta ? <> a <span className="text-emerald-400">{citta}</span></> : ''}</span><br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF5722] to-[#FF7043]">Senza Ponteggi</span>
            </h1>
            <p className="text-lg text-slate-300 mb-8 leading-relaxed max-w-xl mx-auto lg:mx-0">
              Ripariamo, impermeabilizziamo e rinnoviamo il tuo tetto{citta ? ` a ${citta} e provincia` : ''} con tecnologie innovative.
              <strong className="text-white"> Risparmia fino al 70%</strong> rispetto ai metodi tradizionali, senza ponteggi e senza stress.
            </p>
            <div className="grid grid-cols-3 gap-6 mb-8 max-w-lg mx-auto lg:mx-0">
              <div><div className="text-3xl font-bold text-emerald-400" style={{ fontFamily: 'Outfit' }}>2.500+</div><div className="text-sm text-slate-400">Clienti soddisfatti</div></div>
              <div><div className="text-3xl font-bold text-emerald-400" style={{ fontFamily: 'Outfit' }}>15+</div><div className="text-sm text-slate-400">Anni di esperienza</div></div>
              <div><div className="text-3xl font-bold text-emerald-400" style={{ fontFamily: 'Outfit' }}>4.9/5</div><div className="text-sm text-slate-400">Valutazione media</div></div>
            </div>
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-5 text-slate-400 text-sm">
              <div className="flex items-center gap-1.5"><Shield className="w-4 h-4 text-emerald-500" /><span>Assicurazione RC</span></div>
              <div className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-500" /><span>Tecnici certificati</span></div>
              <div className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-emerald-500" /><span>Intervento in 48h</span></div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.2 }} className="lg:col-span-5">
            <div className="bg-white rounded-2xl p-8 shadow-2xl shadow-black/10 relative overflow-hidden">
              <div className="absolute -top-20 -right-20 w-40 h-40 bg-[#FF5722]/5 rounded-full blur-3xl" />
              <div className="text-center mb-6 relative z-10">
                <h2 className="text-xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Outfit' }}>Sopralluogo + Preventivo Gratis</h2>
                <p className="text-slate-500 text-sm">Ti richiamiamo entro 24 ore. Nessun impegno.</p>
              </div>
              <form onSubmit={handleQuickSubmit} className="space-y-4 relative z-10" data-testid="hero-lead-form">
                <input type="text" name="website" value={quickForm.website} onChange={(e) => setQuickForm({ ...quickForm, website: e.target.value })} tabIndex={-1} autoComplete="off" aria-hidden="true" style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }} />
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input data-testid="hero-form-name" type="text" placeholder="Il tuo nome" value={quickForm.name} onChange={(e) => setQuickForm({...quickForm, name: e.target.value})} className="pl-10 h-12 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 focus:ring-2 focus:ring-[#FF5722] focus:border-transparent rounded-xl" required />
                </div>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input data-testid="hero-form-phone" type="tel" placeholder="Numero di telefono" value={quickForm.phone} onChange={(e) => setQuickForm({...quickForm, phone: e.target.value})} className="pl-10 h-12 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 focus:ring-2 focus:ring-[#FF5722] focus:border-transparent rounded-xl" required />
                </div>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input data-testid="hero-form-email" type="email" placeholder="La tua email" value={quickForm.email} onChange={(e) => setQuickForm({...quickForm, email: e.target.value})} className="pl-10 h-12 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 focus:ring-2 focus:ring-[#FF5722] focus:border-transparent rounded-xl" required />
                </div>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 z-10" />
                  <Select value={quickForm.province} onValueChange={(v) => setQuickForm({...quickForm, province: v})}>
                    <SelectTrigger data-testid="hero-form-province" className="pl-10 h-12 bg-slate-50 border-slate-200 text-slate-900 rounded-xl [&>span]:text-slate-400">
                      <SelectValue placeholder="Seleziona provincia" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60 bg-white border-slate-200">
                      {provinces.map((p) => (<SelectItem key={p} value={p} className="text-slate-900 hover:bg-[#FF5722]/5">{p}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <button data-testid="hero-lead-form-submit" type="submit" disabled={isSubmitting} className="w-full bg-[#FF5722] hover:bg-[#FF7043] disabled:opacity-50 text-white font-bold text-base py-3.5 rounded-xl transition-all duration-300 shadow-[0_4px_20px_rgba(255,87,34,0.3)] hover:shadow-[0_4px_30px_rgba(255,87,34,0.45)] hover:-translate-y-0.5">
                  {isSubmitting ? 'Invio in corso...' : 'Richiedi Sopralluogo Gratuito'}
                </button>
              </form>
              <div className="mt-5 space-y-2 relative z-10">
                <div className="flex items-center gap-2 text-xs text-slate-500"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /><span>100% gratuito e senza impegno</span></div>
                <div className="flex items-center gap-2 text-xs text-slate-500"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /><span>I tuoi dati sono al sicuro (GDPR compliant)</span></div>
              </div>
              <div className="mt-6 pt-5 border-t border-slate-100 text-center relative z-10">
                <p className="text-xs text-slate-400 mb-1">Preferisci chiamare?</p>
                <a href="tel:800864621" className="inline-flex items-center gap-2 text-lg font-bold text-[#FF5722] hover:text-[#FF7043] transition-colors"><Phone className="w-4 h-4" />800 86 46 21</a>
                <p className="text-xs text-slate-400 mt-0.5">Numero Verde Gratuito</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
